import {get} from 'lodash-es';
import moment from 'moment/moment';
import React, {useState, FC} from 'react';
import ReactQuill from 'react-quill';
import {useDispatch, useSelector} from 'react-redux';

import {InfoTableModal} from 'components/table/info-table-modal';
import {IFieldMeta} from 'entities/metadata';
import {DATA_MODULE} from 'modules/data';
import {createActionGetFile, setData} from 'modules/data/data-actions';
import {ReactComponent as ComparisonIcon} from 'shared/assets/comparison.svg';
import {ReactComponent as ExtraInfo} from 'shared/assets/extra-info.svg';
import {DATE_WITH_TIME_DOTS_SECONDS_24} from 'shared/constants/date-format';
import {EEntityType} from 'shared/constants/entities';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {ERequestType} from 'shared/types/requests';
import {performRequest, showMessageFromResponse} from 'shared/utils';
import {TAppState} from 'store/config/types';
import {openModal} from 'store/slices/modals-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {StatusCodeIcons} from './additional-information-cell.constants';
import './additional-information-cell.less';

interface IAdditionalInformationCellProps {
    record: any;
    entityName: string;
    parentEntityName: string;
    field?: IFieldMeta;
}

interface IInfoTableModalData {
    createdDate?: Date;
    action?: string;
    status?: string;
    createdBy?: string;
    comment?: string;
}

export const AdditionalInformationCell: FC<IAdditionalInformationCellProps> = ({
    field,
    record,
    entityName,
    parentEntityName,
}: IAdditionalInformationCellProps) => {
    const dispatch = useDispatch();
    const data = useSelector(
        (state: TAppState) => state[DATA_MODULE]?.[parentEntityName]?.[EEntityType.TABLE]?.rows,
    );

    const {hasUserRoleFunction} = useUserRoleFunctions({sections: [entityName]});

    const statusCode = field?.dependentsFieldKey && get(record, field.dependentsFieldKey);
    const attachId = field?.requestKey && get(record, field?.requestKey);
    const Icon = statusCode ? StatusCodeIcons[statusCode] : null;
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [tableData, setTableData] = useState<IInfoTableModalData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleGetInfoForTable = async () => {
        const referenceUrl = `documents/${entityName}/workflow/${record?.id}`;
        setIsModalOpen(true);
        setIsLoading(true);
        try {
            const response = await performRequest({
                url: referenceUrl,
                method: ERequestType.GET,
            });

            const formattedData = response?.data.map((item: IInfoTableModalData) => ({
                ...item,
                createdDate: item?.createdDate ? moment(item?.createdDate).format(DATE_WITH_TIME_DOTS_SECONDS_24) : '',
            }));
            setTableData(formattedData);
        } catch (e) {
            setIsModalOpen(false);
            showMessageFromResponse({response: e.response, isError: true});
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadLog = () => {
        const referenceUrl = `${field?.referenceUrl}/${attachId}`;
        dispatch(
            createActionGetFile({referenceUrl, requestType: ERequestType.GET}),
        );
    };

    const handlerClick = () => {
        const newData = data || [];
        if (field?.modalName) {
            dispatch(openModal({modalName: field?.modalName, parentEntityName}));
        }

        if (!newData || !data?.length) {
            dispatch(setData({
                entityName: parentEntityName,
                entityType: EEntityType.TABLE,
                loading: false,
                data: {
                    rows: [record],
                },
            }));
            return;
        }

        const row: any = record;
        const index = newData?.findIndex((item: any) => row.id === item?.id);
        if (index === -1 && data) {
            dispatch(setData({
                entityName: parentEntityName,
                entityType: EEntityType.TABLE,
                loading: false,
                data: {
                    rows: [...data, record],
                },
            }));
        }
    };
    return (
        <div className="additional-information">
            {field?.key && get(record, field.key) && (
                <ComparisonIcon
                    onClick={handlerClick}
                    className="comparison-icon"
                />
            )}
            {Icon && attachId && (
                <Icon
                    onClick={handleDownloadLog}
                    className="download-log-icon"
                />
            )}
            {Icon && record?.id && hasUserRoleFunction(EUserRoleFunction.VIEW_DOC_WF) && (
                <ExtraInfo
                    onClick={handleGetInfoForTable}
                    className="extra-info"
                />
            )}
            {isModalOpen && (
                <InfoTableModal<IInfoTableModalData>
                    onClose={() => setIsModalOpen(false)}
                    dataSource={tableData}
                    isLoading={isLoading}
                    columns={[
                        {title: 'Дата и время', dataIndex: 'createdDate', key: 'createdDate'},
                        {title: 'Действие', dataIndex: 'action', key: 'action'},
                        {title: 'Статус', dataIndex: 'status', key: 'status'},
                        {title: 'Инициатор', dataIndex: 'createdBy', key: 'createdBy'},
                        {
                            title: 'Комментарий',
                            dataIndex: 'comment',
                            key: 'comment',
                            width: '30vw',
                            render: text => (
                                <ReactQuill
                                    value={text}
                                    readOnly
                                    modules={{toolbar: false}}
                                />
                            ),
                        },
                    ]}
                    modalTitle="Размещение и публикация документa (workflow)"
                />
            )}
        </div>
    );
};
